/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { memo, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { TooltipData } from "types";
import { getSymbols } from "./symbols";
import { M_TABLE, C_TABLE, P_TABLE } from "./consts";
import styles from "./styles";

interface IProps {
  roadClass: string;
  tooltips: TooltipData | null;
}

type TableData = Array<ReactNode | string>[];

const transposeRows = (table: TableData) => table[0].slice(1).map((_, i) => table.map((row) => row[i + 1]));

const TableRoadClasses: React.FC<IProps> = ({ roadClass, tooltips }) => {
  const { t } = useTranslation();

  const symbols = useMemo(() => getSymbols({ tooltips, roadClass, t }), [roadClass, tooltips, t]);

  const MTable: TableData = useMemo(() => [["", symbols.Lav, symbols.Uo, symbols.Ul, symbols.fTI, symbols.Rei], ...M_TABLE], [symbols]);
  const CTable: TableData = useMemo(() => [["", symbols.Eav, symbols.UoE, symbols.fTI], ...C_TABLE], [symbols]);
  const PTable: TableData = useMemo(() => [["", symbols.Eav, symbols.Emin, symbols.Evmin, symbols.Escmin, symbols.fTI], ...P_TABLE], [symbols]);

  const tableConfig = useMemo<Record<string, { table: TableData; caption: string }>>(() => ({
    m: { table: MTable, caption: t("Requirements for motorized traffic (M classes)") },
    c: { table: CTable, caption: t("Requirements for conflict areas (C classes)") },
    p: { table: PTable, caption: t("Requirements for low-speed areas (users of the traffic area: motor vehicles/pedestrians/cyclists)") },
  }), [CTable, MTable, PTable, t]);

  const { table, caption } = tableConfig[roadClass] ?? tableConfig.p;
  const header = useMemo(() => table.map((row) => row[0]), [table]);
  const rows = useMemo(() => transposeRows(table), [table]);

  return (
    <Box component="table" sx={styles.table} className="RlxTableRoadClasses">
      <caption>{t(caption)}</caption>
      <thead>
        <tr>
          {header.map((header, index) => (
            <th key={index}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  );
};

TableRoadClasses.displayName = "TableRoadClasses";

export default memo(TableRoadClasses);
